import { CancelablePromise, makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import FinancialAnalysisResource from 'App/babel/resources/FinancialAnalysis';
import type { FinancialAnalysisResult } from 'Resources/FinancialAnalysis';
import { useTranslation } from 'Components/Helpers/translate';
import React, { useEffect, useRef, useState } from 'react';
import logError from 'Helpers/logError';
import AIInsights from 'App/components/AIInsights';
import BemClass from '@upsales/components/Utils/bemClass';

import './FinancialAIAnalysis.scss';

const fetcher = (fetcherProps: { clientId: string }) => {
	return FinancialAnalysisResource.get(fetcherProps.clientId);
};

const FinancialAIAnalysis = ({ clientId }: { clientId: string }) => {
	const { t } = useTranslation();
	const fetcherData = useRef<CancelablePromise<{ data: FinancialAnalysisResult }> | null>(null);
	const classes = new BemClass('FinancialAIAnalysis');

	const [data, setData] = useState<FinancialAnalysisResult | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		fetcherData.current = makeCancelable(fetcher({ clientId }));
		setIsLoading(true);
		fetcherData.current.promise
			.then(({ data }) => {
				setData(data);
			})
			.catch(e => {
				logError(e, 'FinancialAIAnalysis: Failed to fetch data');
			})
			.finally(() => {
				setIsLoading(false);
			});

		return () => {
			if (fetcherData.current) {
				fetcherData.current.cancel();
			}
		};
	}, [clientId]);

	const { positive, negative, risks, recommendations } = data ?? {
		positive: null,
		negative: null,
		risks: null,
		recommendations: null
	};

	const insights = [
		{
			icon: 'plus' as const,
			color: 'green' as const,
			text: positive ?? '',
			title: t('account.aiFinancialAnalysis.positive')
		},
		{
			icon: 'minus' as const,
			color: 'red' as const,
			text: negative ?? '',
			title: t('account.aiFinancialAnalysis.negative')
		},
		{
			icon: 'warning' as const,
			color: 'yellow' as const,
			text: risks ?? '',
			title: t('account.aiFinancialAnalysis.risks')
		},
		{
			icon: 'star' as const,
			color: 'blue' as const,
			text: recommendations ?? '',
			title: t('account.aiFinancialAnalysis.recommendations')
		}
	];
	return (
		<div className={classes.b()}>
			<AIInsights
				type="financialAnalysis"
				isLoading={isLoading}
				title={t('account.aiFinancialAnalysis.title')}
				insights={insights}
				extra={JSON.stringify({ prospectingId: data?.prospectingId ?? '' })}
			/>
		</div>
	);
};

export default FinancialAIAnalysis;
